'use client';

import { Popover, PopoverButton, PopoverPanel, RadioGroup } from '@headlessui/react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import BDRadio from '@shared/components/elements/bd-gold-radio';
import BDImage from '@shared/components/elements/bd-image';
import { BDSubtitle } from '@shared/components/elements/title';
import { DIAMOND_IMAGE_SIZES, SECOND_DIAMOND_DISCOUNT_RATIO } from '@shared/lib/constants';
import { DiamondVariant } from '@shared/lib/shopify/types/shopify-native';
import { rPercentage } from '@shared/lib/utils/formats';
import { getMoneyRange, renderMoney } from '@shared/lib/utils/money';
import clsx from 'clsx';

import { calculateDiamondImageSize } from '@shared/lib/utils/product-utils';
import ringBackgroundImage from '@shared/public/images/cut/ring-background.png';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';

const DiamondPicker = ({
  cutVariantLists,
  preselectedShape,
  supportedShapes,
  supportedCarats,
  showQuantity = false,
  showPreview = true,
  onSelectShape,
  onSelectVariant,
}: {
  cutVariantLists: Record<string, DiamondVariant[]>;
  preselectedShape?: string | null;
  supportedShapes?: string[];
  supportedCarats?: number[];
  showQuantity?: boolean;
  showPreview?: boolean;
  onSelectShape?: (shape: string) => void;
  onSelectVariant: (variant: DiamondVariant) => void;
}) => {
  const [selectedCut, setSelectedCut] = useState<string | null>(preselectedShape || null);
  const [selectedCarat, setSelectedCarat] = useState<number | null>(null);
  const sizeVariants = cutVariantLists[(selectedCut || 'Round')!]!;
  const displayVariant = sizeVariants.find((o) => o.carat === (selectedCarat || 1))!;
  const displayVariantImageSize = calculateDiamondImageSize(
    displayVariant.carat,
    displayVariant.shape
  );

  const scrollToCarat = useCallback(() => {
    const caratElement = document.getElementById('carat');
    if (caratElement) {
      // Find the dialog's scrollable container
      const dialogContent =
        caratElement.closest('.overflow-y-auto') ||
        caratElement.closest('.overflow-auto') ||
        document.querySelector('.dialog-content'); // adjust selector based on your dialog structure

      if (dialogContent) {
        const stickyHeader = dialogContent.querySelector('.sticky.top-0');
        const stickyHeight = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;

        // Get the element's position relative to the dialog container
        const rect = caratElement.getBoundingClientRect();
        const containerRect = dialogContent.getBoundingClientRect();

        // Calculate scroll position relative to the dialog container
        const scrollPosition = rect.top - containerRect.top - stickyHeight - 10;

        // Check if the element is in view within the dialog
        const isInView = rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;

        if (!isInView) {
          dialogContent.scrollTo({
            top: dialogContent.scrollTop + scrollPosition,
            behavior: 'smooth',
          });
        }
      }
    }
  }, []);

  useEffect(() => {
    if (preselectedShape) {
      setSelectedCut(preselectedShape || null);
    }
  }, [preselectedShape]);

  // When the carat and cut are both selected, call back to the parent to select the variant
  useEffect(() => {
    if (selectedCut) {
      onSelectShape?.(selectedCut);
      if (selectedCarat) {
        onSelectVariant(displayVariant);
      }
    }
  }, [selectedCut, selectedCarat, onSelectShape, onSelectVariant, displayVariant]);

  return (
    <section className="@container grid grid-cols-1 gap-y-4 md:grid-cols-3">
      {showPreview && (
        <div className="sticky top-0 z-10 bg-white md:z-0 md:px-2 md:pr-8">
          <BDSubtitle className="mb-4 md:hidden">Customize your Bonded Diamond™</BDSubtitle>
          <div className="sticky top-6 z-10">
            <div className="relative aspect-[16/9] w-full md:mt-4 md:aspect-square">
              <BDImage
                src={ringBackgroundImage}
                alt="Ring background"
                className="absolute inset-0 h-full w-full rounded-xl object-cover object-center"
              />
              <Image
                src={displayVariant.image.url}
                alt={displayVariant.image.altText || displayVariant.title}
                width={displayVariant.image.width}
                height={displayVariant.image.height}
                style={{
                  width: `${displayVariantImageSize}%`,
                }}
                className={`shadow-bd-dark absolute left-[52.5%] top-[41%] aspect-square -translate-x-1/2 -translate-y-1/2 rotate-[16deg] transform object-cover object-center drop-shadow-md md:top-[44.5%]`}
              />
              <p className="text-bd-light bottom-1/8 absolute left-1/2 z-40 w-3/4 -translate-x-1/2 text-center text-sm font-normal">
                Preview the selected Bonded Diamond™.
              </p>
            </div>
          </div>
        </div>
      )}
      <div
        className={clsx(
          'flex flex-col gap-y-6',
          showPreview ? 'md:col-span-2 md:ml-4' : 'md:col-span-full'
        )}
      >
        <div key="shape" className="flex flex-col gap-4 rounded-xl">
          <div className="border-bd-secondary flex flex-row items-center gap-1 border-b bg-white px-2 py-1">
            <h4 className="text-normal flex flex-row items-center gap-2 font-normal">
              Choose your diamond's shape
            </h4>
            <Popover className="relative">
              <PopoverButton className="flex items-center justify-center">
                <QuestionMarkCircleIcon className="h-5 w-5" />
              </PopoverButton>
              <PopoverPanel className="bg-bd-light absolute left-1/2 top-full z-10 mt-2 w-80 -translate-x-1/2 transform rounded-sm p-2 text-sm shadow-sm">
                <p className="font-semibold">Shape (Cut) </p>
                <p className="">
                  The shape of a polished diamond is also called diamond cut. Bonded Diamond™
                  supports all the popular diamond shapes. The price difference among different
                  shapes are mainly due to the differences of raw diamond size and the complexity of
                  the cutting process.
                </p>
              </PopoverPanel>
            </Popover>
            <span className="font-light capitalize">{selectedCut && `: ${selectedCut}`}</span>
          </div>
          <RadioGroup
            name="cut"
            id="cut"
            className="@[42em]:grid-cols-5 grid grid-cols-3 gap-x-2 gap-y-2"
            value={selectedCut}
            onChange={(value) => {
              setSelectedCut(value);
              scrollToCarat();
            }}
          >
            {Object.entries(cutVariantLists).map(([shape, variants], idx) => (
              <BDRadio
                value={shape}
                key={shape}
                disabled={supportedShapes && !supportedShapes.includes(shape)}
                className="data-disabled:opacity-50"
              >
                <BDImage src={variants[0]!.image} className="aspect-square w-12" />

                <p className="text-sm">{shape}</p>
                <p className="text-bd-dark text-center text-xs sm:text-xs">
                  Starting at{' '}
                  {renderMoney(getMoneyRange(variants.map((variant) => variant.price)).min)}
                </p>
              </BDRadio>
            ))}
          </RadioGroup>
          {/* <div className="grid grid-cols-7">
          <p className="col-start-1 text-center text-xs text-bd-primary">Best Value</p>
          <p className="col-start-4 text-center text-xs text-bd-primary">Most Popular</p>
        </div> */}
        </div>
        <div id="carat" key="carat" className="flex flex-col gap-4">
          <div className="border-bd-secondary flex flex-row items-center gap-1 border-b bg-white px-2 py-1">
            <h4 className="text-normal flex flex-row items-center gap-2 font-normal">
              Choose your desired carat size
            </h4>
            <Popover className="relative">
              <PopoverButton className="flex items-center justify-center">
                <QuestionMarkCircleIcon className="h-5 w-5" />
              </PopoverButton>
              <PopoverPanel className="bg-bd-light absolute left-1/2 top-full z-10 mt-2 w-80 -translate-x-1/2 transform rounded-sm p-2 text-sm shadow-sm">
                <p className="font-semibold">Carat </p>
                <p className="">
                  The international unit of weight, used for measuring diamonds and gemstones. 1
                  carat is equal to 200 milligrams, or 0.2 grams. Because each Bonded Diamond™ is
                  individually made, the final polished diamond may be slightly larger than your
                  specified carat size.
                </p>
              </PopoverPanel>
            </Popover>
            <span className="font-light capitalize">{selectedCarat && `: ${selectedCarat}`}</span>
          </div>
          {supportedCarats && supportedCarats.length < 8 && (
            <p className="text-bd-dark/80 text-sm">
              Some diamond sizes are not available to fit this jewelry item.
            </p>
          )}
          <RadioGroup
            name="carat"
            id="carat"
            className="@[42em]:mb-10 @[42em]:grid-cols-7 grid grid-cols-4 gap-x-2 gap-y-12"
            value={selectedCarat}
            onChange={(value) => {
              setSelectedCarat(Number(value));
            }}
          >
            {sizeVariants.map((variant, index) => (
              <BDRadio
                value={variant.carat.toString()}
                key={variant.carat}
                disabled={
                  !selectedCut ||
                  (supportedCarats &&
                    !supportedCarats.some((carat) => Math.abs(carat - variant.carat) < 0.1))
                }
                className="data-disabled:opacity-50 relative flex justify-end"
              >
                <Image
                  src={variant.image.url}
                  alt={variant.image.altText || variant.title}
                  width={variant.image.width}
                  height={variant.image.height}
                  style={{
                    width: `${DIAMOND_IMAGE_SIZES[index]!}em`,
                    height: `${DIAMOND_IMAGE_SIZES[index]!}em`,
                  }}
                  className="object-cover object-center"
                />
                <div className="flex flex-col items-center justify-center">
                  <p className="whitespace-nowrap text-sm">{variant.caratDisplay}</p>

                  <p className="text-xs">{renderMoney(variant.price)}</p>
                </div>
                <p className="text-bd-primary absolute left-1/2 top-full mt-2 -translate-x-1/2 text-center text-xs">
                  {index === 2 ? 'Best Value' : index === 3 ? 'Most Popular' : ''}
                </p>
              </BDRadio>
            ))}
          </RadioGroup>
        </div>
        {showQuantity && (
          <div key="quantity" className="relative flex flex-col gap-4">
            <div className="border-bd-secondary flex flex-row items-center gap-1 border-b bg-white px-2 py-1">
              <h4 className="text-normal flex flex-row items-center gap-2 font-normal">
                How many diamonds?
              </h4>
              <Popover className="relative">
                <PopoverButton className="flex items-center justify-center">
                  <QuestionMarkCircleIcon className="h-5 w-5" />
                </PopoverButton>
                <PopoverPanel className="bg-bd-light absolute left-1/2 top-full z-10 mt-2 w-80 -translate-x-1/2 transform rounded-sm p-2 text-sm shadow-sm">
                  <p className="font-semibold">Quantity </p>
                  <p className="">
                    How many Bonded Diamonds™ with the above specifications do you want to build?
                    Because it is possible to batch grow multiple diamonds for your order, we will
                    be able to pass that saving back to you. Any 2nd and thereafter Bonded Diamond™
                    are {rPercentage(SECOND_DIAMOND_DISCOUNT_RATIO)} off.
                  </p>
                </PopoverPanel>
              </Popover>
            </div>
            <RadioGroup
              className="grid grid-cols-5 gap-2"
              name="quantity"
              defaultValue="1"
              id="quantity"
            >
              {Array.from({ length: 5 }, (_, index) => (
                <BDRadio
                  value={(index + 1).toString()}
                  key={index + 1}
                  className="data-disabled:opacity-50"
                >
                  <p className="text-xs sm:text-sm md:text-base md:font-normal">{index + 1}</p>
                  <p className="text-xxs font-light opacity-75 sm:text-xs">
                    {index > 0 && `${rPercentage(SECOND_DIAMOND_DISCOUNT_RATIO)} off`}
                  </p>
                </BDRadio>
              ))}
              <p className="text-bd-primary col-start-2 col-end-6 text-center text-xs">
                Any 2nd and thereafter Bonded Diamonds™ are{' '}
                {rPercentage(SECOND_DIAMOND_DISCOUNT_RATIO)} off.
              </p>
            </RadioGroup>
            {/* 
            <p className="text-xxs sm:text-xs">
              Because it is possible to batch grow multiple diamonds for your order, we will be able
              to pass that saving back to you. Any 2nd and thereafter Bonded Diamond™ are{' '}
              {rPercentage(SECOND_DIAMOND_DISCOUNT_RATIO)} off.
            </p> */}
          </div>
        )}
      </div>
    </section>
  );
};

export default DiamondPicker;
